<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { licenseRetryNotice } from '@/services/licenseRetryNotice'
import { computed, onMounted, getCurrentInstance, ref } from 'vue'
import { useDarkMode } from '@/composables/useDarkMode'
import { getAuth } from './utils/authStorage'
import { useAuthStore } from '@/stores/auth'
import ParticleCanvas from '@/components/ParticleCanvas.vue'
import TradeConfirmationModal from '@/components/TradeConfirmationModal.vue'

const { t } = useI18n()
const { proxy } = getCurrentInstance()!
const route = useRoute()
const authStore = useAuthStore()
const isLoading = ref(true)

const currentLayout = computed(() => {
  return route.meta.layout || 'DefaultLayout'
})

// Initialize theme immediately from localStorage (synchronous, no flash)
const { initTheme } = useDarkMode()
initTheme()

// Initialize socket connection on mount
onMounted(async () => {
  const userAuth = getAuth()
  isLoading.value = false
})
</script>

<template>
  <div class="app-root">
    <ParticleCanvas :count="70" :connection-distance="130" :opacity="0.25" />
    <Transition name="fade-loading">
      <div v-if="isLoading" class="loading-screen">
        <div class="loading-logo-wrapper">
          <div class="loading-pulse-ring"></div>
          <div class="loading-pulse-ring delay"></div>
          <img src="/logo-mini.svg" alt="ForMind" class="loading-logo" />
        </div>
      </div>
    </Transition>
    <component v-if="!isLoading" :is="currentLayout">
      <RouterView />
    </component>
    <div v-if="licenseRetryNotice" class="license-retry-notice" role="alert">
      <span>{{ t('licenseRetry.failed') }}</span>
      <button type="button" @click="licenseRetryNotice = false">{{ t('licenseRetry.dismiss') }}</button>
    </div>
    <!-- Global Trade Confirmation Modal -->
    <TradeConfirmationModal />
  </div>
</template>

<style scoped>
.license-retry-notice {
  position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
  z-index: 10000; display: flex; align-items: center; gap: 16px;
  width: max-content; max-width: calc(100vw - 32px); box-sizing: border-box;
  padding: 16px; border: 1px solid #f87171; border-radius: 8px;
  background: #321b21; color: #fff; box-shadow: 0 4px 20px #0005;
}
.license-retry-notice button { color: inherit; background: transparent; border: 1px solid currentColor; border-radius: 4px; padding: 4px 8px; cursor: pointer; }

.app-root {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100vh;
}

.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #101014;
  z-index: 9999;
}

.loading-logo-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
}

.loading-logo {
  width: 48px;
  height: 48px;
  animation: logo-breathe 2s ease-in-out infinite;
  filter: drop-shadow(0 0 12px rgba(74, 158, 255, 0.4));
  z-index: 1;
}

.loading-pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid rgba(74, 158, 255, 0.4);
  animation: pulse-expand 2s ease-out infinite;
}

.loading-pulse-ring.delay {
  animation-delay: 0.6s;
}

@keyframes logo-breathe {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.08);
    opacity: 0.85;
  }
}

@keyframes pulse-expand {
  0% {
    transform: scale(0.5);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

/* Fade transition for loading screen */
.fade-loading-leave-active {
  transition: opacity 0.4s ease;
}

.fade-loading-leave-to {
  opacity: 0;
}
</style>
